'use client';
import { useState, useMemo } from 'react';
import { Markdown } from './Markdown';
import { EmptyState } from './EmptyState';
import { Toc } from './Toc';
import { ModuleCompletionButton } from './ModuleCompletionButton';
import { ModuleQuiz } from './ModuleQuiz';
import { usePathname } from 'next/navigation';

type Heading = { id: string; text: string; level: number };

type ModuleViewerProps = {
  title: string;
  content: string;
  spec: unknown | null;
  moduleId?: string;
};

import { useVoice } from '../components/providers/VoiceProvider';
import { Mic, ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect } from 'react';

// ... existing props ...

export function ModuleViewer({ title, content, spec, moduleId }: ModuleViewerProps) {
  const pathname = usePathname();
  const id = moduleId || pathname?.match(/\/modules\/([^/]+)/)?.[1];
  const [headings, setHeadings] = useState<Heading[]>([]);
  const { sendSystemMessage, sendText, isConnected, startListening, connect, resumeAudioContext } =
    useVoice();

  // ... (useEffect remains same)

  const handleDiscuss = async () => {
    await resumeAudioContext();

    if (!isConnected) {
      // Prompt user to connect first
      // In a real app we might auto-connect, but here we want to ensure permission/context
      const confirmed = window.confirm('Voice assistant is disconnected. Connect now?');
      if (confirmed) {
        await connect();
        // Wait a bit for connection? Or just let the user try again once connected.
        // For now, let's just connect and send the text if it becomes ready quickly.
      } else {
        return;
      }
    }

    // Send context first
    const contextLimit = 8000;
    const truncatedContent =
      content.length > contextLimit
        ? content.substring(0, contextLimit) + '...(truncated)'
        : content;

    sendSystemMessage(
      `User is discussing module: "${title}". Here is the content:\n\n${truncatedContent}`
    );

    sendText(
      `I'd like to discuss the module "${title}". Can you give me a summary or guide me through it?`
    );
  };

  if (!content) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold">{title}</h1>
          <button
            onClick={handleDiscuss}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary border border-primary/20 rounded-md hover:bg-primary/5 transition-colors"
          >
            <Mic className="h-4 w-4" />
            Discuss
          </button>
        </div>
        <EmptyState
          title="No content yet"
          message="This module has no index.md. Add one under /content/<slug>/index.md or use the CTAs to create content."
        />
        {spec != null && (
          <div className="mt-6">
            <h2 className="text-xl font-semibold mb-3">Spec</h2>
            <pre className="bg-muted p-4 rounded-xl text-xs overflow-auto">
              {JSON.stringify(spec, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  }

  // Split content to hide static quiz section if we are going to render it interactively
  const { mainContent, quizContent } = useMemo(() => {
    // Look for "## Assessments" or "### Lesson Check"
    const splitRegex = /(## Assessments|### Lesson Check)/;
    const match = content.match(splitRegex);

    if (match && match.index) {
      return {
        mainContent: content.substring(0, match.index),
        quizContent: content,
      };
    }
    return { mainContent: content, quizContent: null };
  }, [content]);

  // Mobile TOC state
  const [isTocOpen, setIsTocOpen] = useState(false);

  return (
    <div className="w-full max-w-full">
      <div className="flex items-center justify-between mb-6">
        {/* Title is typically in Markdown content, but if we need a header above it: */}
        {/* <h1 className="text-3xl font-bold">{title}</h1> */}
        <div className="ml-auto">
          <button
            onClick={handleDiscuss}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary border border-primary/20 rounded-md hover:bg-primary/5 transition-colors"
          >
            <Mic className="h-4 w-4" />
            Discuss with Guide
          </button>
        </div>
      </div>

      {/* Mobile Table of Contents (Collapsible) */}
      {headings.length > 0 && (
        <div className="lg:hidden mb-6 border border-border rounded-lg overflow-hidden bg-muted/30">
          <button
            onClick={() => setIsTocOpen(!isTocOpen)}
            className="w-full flex items-center justify-between p-3 text-sm font-medium"
          >
            <span>Table of Contents</span>
            {isTocOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {isTocOpen && (
            <div className="p-3 pt-0 border-t border-border">
              <Toc headings={headings} className="static h-auto max-h-none" />
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6 lg:gap-12">
        <article className="min-w-0">
          <Markdown content={mainContent} onHeadingsChange={setHeadings} />

          {/* Interactive Quiz Section */}
          {quizContent && <ModuleQuiz content={quizContent} />}
          {id && (
            <div className="mt-8 pt-8 border-t border-border">
              <ModuleCompletionButton moduleId={id} />
            </div>
          )}
          {spec != null && (
            <div className="mt-8 pt-8 border-t border-border">
              <h2 className="text-xl font-semibold mb-4">Module Specification</h2>
              <pre className="bg-muted p-4 rounded-xl text-xs overflow-auto border border-border">
                {JSON.stringify(spec, null, 2)}
              </pre>
            </div>
          )}
        </article>

        {/* Desktop Sidebar TOC */}
        {headings.length > 0 && (
          <aside className="hidden lg:block shrink-0">
            <div className="sticky top-24">
              <Toc headings={headings} />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
